// src/components/housekeeping/HousekeepingReports.js
import React, { useState, useEffect } from 'react';
import { supabase } from '../../config/supabase.config';

function HousekeepingReports({ staff }) {
  const [reportMonth, setReportMonth] = useState(new Date().getMonth() + 1);
  const [reportYear, setReportYear] = useState(new Date().getFullYear());
  const [attendanceData, setAttendanceData] = useState([]);
  const [taskData, setTaskData] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchReportData();
  }, [reportMonth, reportYear]);

  const fetchReportData = async () => {
    setLoading(true);
    try {
      // Get days in month
      const daysInMonth = new Date(reportYear, reportMonth, 0).getDate();
      const startDate = `${reportYear}-${String(reportMonth).padStart(2, '0')}-01`;
      const endDate = `${reportYear}-${String(reportMonth).padStart(2, '0')}-${daysInMonth}`;

      // Fetch attendance data
      const { data: attendance, error: attendanceError } = await supabase
        .from('housekeeping_attendance')
        .select('*')
        .gte('attendance_date', startDate)
        .lte('attendance_date', endDate);

      if (!attendanceError) {
        setAttendanceData(attendance || []);
      }

      // Fetch task completion data
      const { data: tasks, error: tasksError } = await supabase
        .from('housekeeping_task_log')
        .select('*')
        .gte('task_date', startDate)
        .lte('task_date', endDate);

      if (!tasksError) {
        setTaskData(tasks || []);
      }
    } catch (error) {
      console.error('Error fetching report data:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateStaffReport = (staffMember) => {
    const staffAttendance = attendanceData.filter(a => a.staff_id === staffMember.id);
    const present = staffAttendance.filter(a => a.status === 'present').length;
    const halfDay = staffAttendance.filter(a => a.status === 'half_day').length;
    const absent = staffAttendance.filter(a => a.status === 'absent').length;
    const leave = staffAttendance.filter(a => a.status === 'leave').length;
    
    const totalDays = present + (halfDay * 0.5);
    const salary = staffMember.salary || 0;
    const dailyWage = salary / 30;
    const calculatedSalary = dailyWage * totalDays;

    const staffTasks = taskData.filter(t => t.staff_id === staffMember.id);
    const completedTasks = staffTasks.filter(t => t.completed).length;
    const totalTasks = staffTasks.length;

    return {
      present,
      halfDay,
      absent,
      leave,
      totalDays,
      calculatedSalary,
      completedTasks,
      totalTasks
    };
  };

  const exportReport = () => {
    let csvContent = `Housekeeping Report - ${reportMonth}/${reportYear}\n\n`;
    csvContent += 'Staff Name,Present,Half Day,Absent,Leave,Total Days,Tasks Completed,Salary\n';
    
    staff.forEach(member => {
      const report = calculateStaffReport(member);
      csvContent += `${member.name},${report.present},${report.halfDay},${report.absent},${report.leave},${report.totalDays},${report.completedTasks}/${report.totalTasks},₹${report.calculatedSalary.toFixed(2)}\n`;
    });

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `housekeeping_report_${reportMonth}_${reportYear}.csv`;
    a.click();
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h3>Monthly Report</h3>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <select
            value={reportMonth}
            onChange={(e) => setReportMonth(parseInt(e.target.value))}
            style={{
              padding: '8px',
              border: '1px solid #ddd',
              borderRadius: '5px'
            }}
          >
            {[...Array(12)].map((_, i) => (
              <option key={i+1} value={i+1}>
                {new Date(2024, i).toLocaleDateString('en-US', { month: 'long' })}
              </option>
            ))}
          </select>
          
          <select
            value={reportYear}
            onChange={(e) => setReportYear(parseInt(e.target.value))}
            style={{
              padding: '8px',
              border: '1px solid #ddd',
              borderRadius: '5px'
            }}
          >
            {[2024, 2025, 2026].map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>

          <button
            onClick={exportReport}
            style={{
              padding: '8px 16px',
              background: '#17a2b8',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer'
            }}
          >
            📥 Export CSV
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          Loading report data...
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8f9fa' }}>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>
                  Staff Name
                </th>
                <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>
                  Present
                </th>
                <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>
                  Half Day
                </th>
                <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>
                  Absent
                </th>
                <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>
                  Leave
                </th>
                <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>
                  Total Days
                </th>
                <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>
                  Tasks
                </th>
                <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #dee2e6' }}>
                  Calculated Salary
                </th>
              </tr>
            </thead>
            <tbody>
              {staff.map(member => {
                const report = calculateStaffReport(member);
                return (
                  <tr key={member.id}>
                    <td style={{ padding: '12px', borderBottom: '1px solid #dee2e6' }}>
                      <strong>{member.name}</strong>
                      {member.salary && (
                        <div style={{ fontSize: '11px', color: '#666' }}>
                          Base: ₹{member.salary}/month
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '12px', borderBottom: '1px solid #dee2e6', textAlign: 'center' }}>
                      <span style={{ color: '#28a745', fontWeight: '600' }}>{report.present}</span>
                    </td>
                    <td style={{ padding: '12px', borderBottom: '1px solid #dee2e6', textAlign: 'center' }}>
                      <span style={{ color: '#ffc107', fontWeight: '600' }}>{report.halfDay}</span>
                    </td>
                    <td style={{ padding: '12px', borderBottom: '1px solid #dee2e6', textAlign: 'center' }}>
                      <span style={{ color: '#dc3545', fontWeight: '600' }}>{report.absent}</span>
                    </td>
                    <td style={{ padding: '12px', borderBottom: '1px solid #dee2e6', textAlign: 'center' }}>
                      <span style={{ color: '#17a2b8', fontWeight: '600' }}>{report.leave}</span>
                    </td>
                    <td style={{ padding: '12px', borderBottom: '1px solid #dee2e6', textAlign: 'center' }}>
                      <strong>{report.totalDays}</strong>
                    </td>
                    <td style={{ padding: '12px', borderBottom: '1px solid #dee2e6', textAlign: 'center' }}>
                      {report.totalTasks > 0 ? (
                        <span>
                          {report.completedTasks}/{report.totalTasks}
                          <span style={{
                            marginLeft: '5px',
                            fontSize: '11px',
                            color: report.completedTasks === report.totalTasks ? '#28a745' : '#ffc107'
                          }}>
                            ({Math.round((report.completedTasks / report.totalTasks) * 100)}%)
                          </span>
                        </span>
                      ) : '-'}
                    </td>
                    <td style={{ 
                      padding: '12px', 
                      borderBottom: '1px solid #dee2e6', 
                      textAlign: 'right',
                      fontSize: '16px',
                      fontWeight: '600',
                      color: '#333'
                    }}>
                      ₹{report.calculatedSalary.toFixed(2)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr style={{ background: '#f8f9fa' }}>
                <td colSpan="7" style={{ padding: '12px', fontWeight: '600' }}>
                  Total
                </td>
                <td style={{ 
                  padding: '12px', 
                  textAlign: 'right',
                  fontSize: '18px',
                  fontWeight: '600',
                  color: '#667eea'
                }}>
                  ₹{staff.reduce((sum, member) => {
                    const report = calculateStaffReport(member);
                    return sum + report.calculatedSalary;
                  }, 0).toFixed(2)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {/* Summary Cards */}
      <div style={{ 
        marginTop: '30px',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '15px'
      }}>
        <div style={{
          background: '#e8f5e9',
          padding: '15px',
          borderRadius: '8px'
        }}>
          <h4 style={{ margin: '0 0 10px 0', color: '#2e7d32' }}>Total Present Days</h4>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#2e7d32' }}>
            {attendanceData.filter(a => a.status === 'present').length}
          </div>
        </div>

        <div style={{
          background: '#fff3e0',
          padding: '15px',
          borderRadius: '8px'
        }}>
          <h4 style={{ margin: '0 0 10px 0', color: '#ff6f00' }}>Tasks Completed</h4>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#ff6f00' }}>
            {taskData.filter(t => t.completed).length}/{taskData.length}
          </div>
        </div>

        <div style={{
          background: '#e3f2fd',
          padding: '15px',
          borderRadius: '8px'
        }}>
          <h4 style={{ margin: '0 0 10px 0', color: '#0d47a1' }}>Average Attendance</h4>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#0d47a1' }}>
            {staff.length > 0 ? 
              Math.round((attendanceData.filter(a => a.status === 'present').length / 
              (staff.length * new Date(reportYear, reportMonth, 0).getDate())) * 100) : 0}%
          </div>
        </div>
      </div>
    </div>
  );
}

export default HousekeepingReports;